import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { useUser } from "@/providers/user";
import { DataService } from "@/services/data";
import { Loader2 } from "lucide-react";

import { offerFormSchema, offerFormSchemaWithSeats } from "../forms/offer/_schema";
import { OfferCommonForm } from "../forms/offer/common";

type OfferFormResponse = {
    opportunity_id: number;
    product: number | null;
    available_seats: number;
    apprentissage: boolean;
    contrat_pro: boolean;
    pro_a: boolean;
    rqth: boolean;
    age_max: number | null;
    max_commute: number | null;
    desired_starting_date: string | null;
    job_link: string | null;
    interview_slots_description: string | null;
    found_by_candidate: boolean;
    found_by_csm: boolean;
    active: boolean;
};

type OpportunityEditOfferDialogProps = {
    recordId: string | number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
};

function buildValuesFromOffer(data: OfferFormResponse): z.infer<typeof offerFormSchema> {
    return {
        apprentissage: data.apprentissage,
        contrat_pro: data.contrat_pro,
        pro_a: data.pro_a,
        product: data.product != null ? String(data.product) : "",
        available_seats: data.available_seats ?? 1,
        job_link: data.job_link ?? "",
        interview_slots_description: data.interview_slots_description ?? "",
        rqth: data.rqth,
        age_max_toggle: Boolean(data.age_max),
        age_max: data.age_max,
        max_commute_toggle: Boolean(data.max_commute),
        max_commute: data.max_commute,
        desired_starting_date: data.desired_starting_date ?? undefined,
        active: data.active,
        found_by_candidate: data.found_by_candidate,
        found_by_csm: data.found_by_csm,
        candidate: {
            first_name: "",
            last_name: "",
            email: "",
            phone: ""
        },
        current_job: {
            title: "",
            classification: "",
            level: "",
            hierarchical_coefficient: "",
            starting_date: ""
        }
    };
}

export function OpportunityEditOfferDialog({
    recordId,
    open,
    onOpenChange,
    onSuccess
}: OpportunityEditOfferDialogProps) {
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const { user, has_any_group } = useUser();
    const is_admin = has_any_group(["app_apprenticeship_admin"]);
    const is_cap = has_any_group(["team_apprenticeship_cap", "app_apprenticeship_admin"]);

    const resolver = useMemo(() => zodResolver(offerFormSchemaWithSeats), []);
    const form = useForm<z.infer<typeof offerFormSchema>>({
        mode: "onSubmit",
        resolver
    });

    useEffect(() => {
        if (!open) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        DataService.client
            .url(`/crm/objects/opportunities/${recordId}/offer_form/`)
            .get()
            .json<OfferFormResponse>()
            .then(data => {
                if (cancelled) return;
                form.reset(buildValuesFromOffer(data));
            })
            .catch(() => {
                if (cancelled) return;
                toast.error("Impossible de charger l'offre");
                onOpenChange(false);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // Only reload when the dialog opens on a given record — not when parent
        // callbacks/`form` identities change (that was re-fetching in a loop).
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
    }, [open, recordId]);

    const onSubmit = async (values: z.infer<typeof offerFormSchema>) => {
        setSubmitting(true);
        try {
            // `active` is intentionally omitted: on SF opportunities, activate/deactivate is
            // stage-driven (New / Deprecated), not a separate offer-form field.
            const payload = {
                product: Number(values.product),
                available_seats: values.available_seats ?? 1,
                apprentissage: values.apprentissage,
                contrat_pro: values.contrat_pro,
                pro_a: false,
                rqth: values.rqth,
                found_by_candidate: values.found_by_candidate,
                found_by_csm: values.found_by_csm,
                desired_starting_date: values.desired_starting_date || null,
                job_link: values.job_link || null,
                interview_slots_description: values.interview_slots_description || null,
                age_max: values.age_max_toggle ? values.age_max : null,
                max_commute: values.max_commute_toggle ? values.max_commute : null
            };

            await DataService.client
                .url(`/crm/objects/opportunities/${recordId}/update_opportunity/`)
                .patch(payload)
                .json();

            toast.success("L'offre a été mise à jour");
            onOpenChange(false);
            onSuccess?.();
        } catch (error: unknown) {
            const message =
                (error as { json?: { error?: string; detail?: string } })?.json?.error ||
                (error as { json?: { detail?: string } })?.json?.detail ||
                "Une erreur est survenue lors de la mise à jour de l'offre";
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[750px] max-h-[90vh] md:max-w-screen-lg overflow-auto">
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 outline-none">
                        <DialogHeader>
                            <DialogTitle>Offre</DialogTitle>
                            <DialogDescription>Modifier cette offre pour un poste d'alternant</DialogDescription>
                        </DialogHeader>

                        {loading ? (
                            <div className="flex items-center justify-center py-10">
                                <Loader2 className="h-6 w-6 animate-spin" />
                            </div>
                        ) : (
                            <OfferCommonForm
                                form={form}
                                is_cap={is_cap}
                                is_admin={is_admin}
                                user={user}
                                showActiveToggle={false}
                                showAvailableSeats
                            />
                        )}

                        <DialogFooter>
                            <Button type="button" onClick={form.handleSubmit(onSubmit)} disabled={submitting || loading}>
                                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Modifier l'offre
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
